/* istanbul ignore file */

import { useMemo, useRef } from 'react';
import type { Group } from 'three';
import { Vector3 } from 'three';
import { useFrame } from '@react-three/fiber';
import { Line, Html } from '@react-three/drei';

type GridHintProps = {
  gridSize: number;
  cellSize: number;
  modelSize: number;
  padding?: number;
};

const calculatePosition = (cameraPosition: Vector3, gridSize: number, cellSize: number, padding: number) => {
  const x = cameraPosition.x > 0 ? gridSize / 2 - cellSize + padding : -gridSize / 2 + padding;
  const z = cameraPosition.z > 0 ? gridSize / 2 + padding : -gridSize / 2 - padding;
  return { x, z };
};

const calculateRotation = (cameraPosition: Vector3) => {
  const angleY = Math.atan2(cameraPosition.x, cameraPosition.z);
  const lineRotationX = cameraPosition.z > 0 ? -Math.PI / 2 : Math.PI / 2;
  return { angleY, lineRotationX };
};

const createPoints = (cellSize: number) => [
  [-cellSize / 2, 0, 0],
  [cellSize / 2, 0, 0],
];

const createVerticalPoints = (cellSize: number) => [
  [
    [-cellSize / 2, cellSize / 4, 0],
    [-cellSize / 2, 0, 0],
  ],
  [
    [cellSize / 2, cellSize / 4, 0],
    [cellSize / 2, 0, 0],
  ],
];

const formatMeasurement = (cellSize: number): string => `${cellSize} mm`;

const getTextPosition = (cellSize: number): [number, number, number] => {
  switch (true) {
    case cellSize < 1:
      return [0, 0.05, 0];
    case cellSize < 5:
      return [0, 0.15, 0];
    case cellSize < 10:
      return [0, 0.25, 0];
    default:
      return [0, 2, 0];
  }
};

const getFontSize = (cellSize: number): string => {
  switch (true) {
    case cellSize < 1:
      return '16px';
    case cellSize < 5:
      return '18px';
    case cellSize < 10:
      return '20px';
    default:
      return '24px';
  }
};

export const GridHint = ({ gridSize, cellSize, modelSize, padding = 5 }: GridHintProps) => {
  const groupRef = useRef<Group>(null);
  const lineRef = useRef<Group>(null);
  const fontSize = useMemo(() => getFontSize(cellSize), [cellSize]);
  const points = createPoints(cellSize);
  const [verticalPoints1, verticalPoints2] = createVerticalPoints(cellSize);
  const measurementText = formatMeasurement(cellSize);
  const textPosition = useMemo(() => getTextPosition(cellSize), [cellSize]);

  useFrame(({ camera }) => {
    if (groupRef.current) {
      const scaledPadding = padding * (cellSize / 10);
      const { x, z } = calculatePosition(camera.position, gridSize, cellSize, scaledPadding);
      const { lineRotationX } = calculateRotation(camera.position);

      groupRef.current.position.set(x, -modelSize / 2, z);
      lineRef.current.rotation.set(lineRotationX, 0, 0);
    }
  });

  return (
    <group ref={groupRef}>
      <group ref={lineRef}>
        <Line points={points.map(p => new Vector3(...p))} color="rgb(211, 211, 211)" lineWidth={1} />
        <Line points={verticalPoints1.map(p => new Vector3(...p))} color="rgb(211, 211, 211)" lineWidth={1} />
        <Line points={verticalPoints2.map(p => new Vector3(...p))} color="rgb(211, 211, 211)" lineWidth={1} />
      </group>
      <Html position={textPosition} center>
        <div
          className="whitespace-nowrap"
          style={{
            color: 'rgb(111, 111, 111)',
            fontSize,
          }}
        >
          {measurementText}
        </div>
      </Html>
    </group>
  );
};
